/**
 * components/DataTable/StackedRow
 * ──────────────────────────────────────────────────────────────────────────
 * What a table row becomes below 1024px.
 *
 * The design is a desktop console and states plainly that no mobile layout was
 * drawn. Rather than invent a second information architecture, this renders
 * the SAME columns, in the SAME order, as label/value pairs inside a card: the
 * first column becomes the card's title (it is the identifying one in every
 * table here — holder, request, event), and the rest stack beneath it.
 *
 * Nothing is dropped, nothing is reordered, and no data appears on a phone
 * that is not on the desktop table.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { memo, type ReactNode } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { sans, size as fontSize } from '../../theme/typography';
import { pointerCursor } from '../../theme/webStyle';
import Icon from '../Icon';

export interface StackedField {
  label: string;
  content: ReactNode;
}

export interface StackedRowProps {
  /** The identifying column, rendered as the card's heading. */
  title: ReactNode;
  fields: StackedField[];
  onPress?: () => void;
  chevron?: boolean;
}

function StackedRowBase({ title, fields, onPress, chevron = false }: StackedRowProps) {
  const interactive = !!onPress;

  return (
    <Pressable
      onPress={onPress}
      disabled={!interactive}
      accessibilityRole={interactive ? 'button' : undefined}
      style={[styles.card, interactive && pointerCursor]}
    >
      <View style={styles.header}>
        <View style={styles.title}>{title}</View>
        {chevron && interactive && (
          <Icon name="chevronRight" size={13} color={neutral.grey300} strokeWidth={1.4} />
        )}
      </View>

      {fields.map((field) => (
        <View key={field.label} style={styles.field}>
          <Text style={styles.label} numberOfLines={1}>
            {field.label}
          </Text>
          <View style={styles.value}>{field.content}</View>
        </View>
      ))}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: 8,
    padding: 13,
    borderWidth: 1,
    borderColor: neutral.border,
    borderRadius: radii.card,
    backgroundColor: neutral.surface,
  },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  title: { flex: 1, minWidth: 0 },
  field: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 },
  label: {
    ...sans('500'),
    fontSize: fontSize.tableHeader,
    letterSpacing: 0.63,
    textTransform: 'uppercase',
    color: neutral.grey600,
    flexShrink: 0,
    // Aligns the label's cap-height with the first line of the value.
    paddingTop: 2,
  },
  value: { flex: 1, minWidth: 0, alignItems: 'flex-end' },
});

export const StackedRow = memo(StackedRowBase);
export default StackedRow;
